/**
 * The house — what `app.lumnian.com` is for.
 *
 * The products live at their own hosts and show only their own work, which
 * leaves a question this screen answers: where do you go to see everything you
 * have, change a setting that spans all of it, or find out what you have not
 * bought? Without it, `app` is the leftover host with no job, and "Lumnian"
 * stops meaning anything.
 *
 * NO SUBSCRIBE BUTTON HERE. A held product's card says which plan it is on and
 * links to the product; the balance, the plan table and (when Stripe lands)
 * the buttons live on /billing, one page for one account-wide balance. A
 * Subscribe button that opened nothing would be worse than no button — it
 * promises a transaction the product cannot complete. Unheld products say what
 * they are and stop there.
 */
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../contexts/EntitlementContext';
import { creditsLine } from '../billing/credits';
import { PRODUCT_INFO, PRODUCT_ORDER, shellUrl } from '../shell/products';
import type { Product } from '../types';

function ProductCard({
  product,
  held,
  lapsed,
  planLabel,
  retiring = false,
}: {
  product: Product;
  held: boolean;
  lapsed: boolean;
  planLabel?: string | null;
  /** Sunsetting: you keep it for now, and the card says so. */
  retiring?: boolean;
}) {
  const info = PRODUCT_INFO[product];
  if (!info) return null;

  const href = shellUrl(info.shell, window.location.hostname, window.location.origin);

  const body = (
    <>
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {info.name}
        </h3>
        {held && (
          <span className="text-[10px] font-semibold tracking-widest uppercase px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            {planLabel ? `${planLabel} plan` : 'Active'}
          </span>
        )}
        {retiring && (
          <span className="text-[10px] font-semibold tracking-widest uppercase px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            Retiring
          </span>
        )}
        {lapsed && (
          /* Lapsed rather than absent, because "your subscription stopped" and
             "you never had this" are different facts and only one of them is
             the user's problem to fix. */
          <span className="text-[10px] font-semibold tracking-widest uppercase px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            Lapsed
          </span>
        )}
      </div>
      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
        {info.headline}
      </p>
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
        {info.tagline}
      </p>
      <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
        {info.unlocks.join(' · ')}
      </p>
    </>
  );

  if (!held) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-5 opacity-75">
        {body}
      </div>
    );
  }

  return (
    <a
      href={href}
      className="block rounded-lg border border-gray-200 dark:border-gray-700 p-5 bg-white dark:bg-gray-800 hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
    >
      {body}
      <p className="mt-3 text-sm font-medium text-blue-600 dark:text-blue-400">
        Open {info.name} →
      </p>
    </a>
  );
}

export default function Launcher() {
  const { user } = useAuth();
  const { held, available, entitlements, loading, plans, planOf, credits, priceBook, catalogOf } =
    useEntitlements();

  // The catalog comes from the server, and it is WHAT THIS USER MAY SEE: a
  // private product someone else uses is not in the list. So the presentation
  // order is only a placeholder while the call is in flight — an empty answer
  // must show nothing, not everything.
  const catalog: Product[] = loading ? PRODUCT_ORDER : available;
  const lapsedProducts = entitlements.filter((e) => !e.active).map((e) => e.product);

  return (
    <div className="max-w-4xl mx-auto">
      {/* NO EXPLANATORY SUBTITLE. It said "Each product is its own app.
          Everything you make lands on the same shelf", which is a sentence about
          our architecture, addressed to nobody: a person who opens this wants to
          get somewhere, and the cards below already show them where. Copy that
          narrates the design is a sign the design is not doing the work. */}
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {user?.first_name ? `Hello, ${user.first_name}` : 'Lumnian'}
        </h1>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        {catalog.map((product) => (
          <ProductCard
            key={product}
            product={product}
            held={held.includes(product)}
            lapsed={lapsedProducts.includes(product)}
            planLabel={held.includes(product) ? plans[planOf(product)]?.label ?? null : null}
            retiring={catalogOf(product)?.availability === 'SUNSETTING'}
          />
        ))}
      </div>

      {/* ONE BALANCE for the account, so it sits above the products rather
          than on one of them. */}
      {!loading && held.length > 0 && (
        <p className="mt-6 text-sm text-gray-600 dark:text-gray-300" data-testid="launcher-credits">
          {creditsLine(credits, priceBook)} ·{' '}
          <Link to="/billing" className="text-blue-600 dark:text-blue-400 hover:underline">
            Credits &amp; billing
          </Link>
        </p>
      )}

      {!loading && held.length === 0 && (
        <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
          Nothing active yet. What you have already made is still on{' '}
          <Link to="/artifacts" className="text-blue-600 dark:text-blue-400 hover:underline">
            the shelf
          </Link>
          .
        </p>
      )}

      <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <Link to="/artifacts" className="text-gray-600 dark:text-gray-300 hover:underline">
          Artifacts
        </Link>
        <Link to="/settings" className="text-gray-600 dark:text-gray-300 hover:underline">
          Settings
        </Link>
        <Link to="/billing" className="text-gray-600 dark:text-gray-300 hover:underline">
          Billing
        </Link>
        <Link to="/account" className="text-gray-600 dark:text-gray-300 hover:underline">
          Account
        </Link>
      </div>
    </div>
  );
}
